import './DeviceBreakout.scss';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Page } from '@Components/Page';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { CellRenderer, StickyTable } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useSetError } from '../../redux/modals';
import {
  COLUMN_WIDTH,
  CompanyOption,
  DeviceCell,
  HEADER_HEIGHT,
  LEFT_WIDTH,
  ModalData,
  NetworkOption,
  ROW_HEIGHT,
  SelectedGroup,
  TOP_HEIGHT
} from './device-breakout-constants';
import { buildTableRows, formatNetworkLabel, initDates } from './device-breakout-utils';
import { DeviceBreakoutModal } from './DeviceBreakoutModal/DeviceBreakoutModal';
import {
  useCompanyFlightData,
  useCompanyOptions,
  useDeviceBreakoutByCompany,
  useDeviceBreakoutByNetwork,
  useNetworkOptions
} from './use-device-breakout';

interface DeviceBreakoutGroupPickerProps {
  selectedGroup: SelectedGroup | undefined;
  setSelectedGroup: (group: SelectedGroup) => void;
  companyOptions?: CompanyOption[];
  networkOptions?: NetworkOption[];
}

const DeviceBreakoutGroupPicker = ({
  selectedGroup,
  setSelectedGroup,
  companyOptions,
  networkOptions
}: DeviceBreakoutGroupPickerProps): ReactElement | null => {
  if (!selectedGroup) {
    return null;
  }

  if ('network' in selectedGroup) {
    const options = networkOptions ?? [];
    return (
      <div className='device-breakout-company-picker'>
        <div className='device-breakout-select'>
          <Dropdown
            size={Size.LG}
            searchable
            value={JSON.stringify(selectedGroup)}
            options={options.map(option => ({
              value: JSON.stringify(option),
              label: formatNetworkLabel(option)
            }))}
            onChange={newValue => {
              const found = options.find(option => JSON.stringify(option) === newValue);
              if (found) {
                setSelectedGroup(found);
              }
            }}
          />
        </div>
      </div>
    );
  }

  const options = companyOptions ?? [];
  return (
    <div className='device-breakout-company-picker'>
      <div className='device-breakout-select'>
        <Dropdown
          size={Size.LG}
          searchable
          value={selectedGroup.cid}
          options={options.map(option => ({ value: option.cid, label: option.name }))}
          onChange={newValue => {
            const found = options.find(option => option.cid === newValue);
            if (found) {
              setSelectedGroup(found);
            }
          }}
        />
      </div>
    </div>
  );
};

interface DeviceBreakoutActionsProps {
  selectedGroup: SelectedGroup | undefined;
  setSelectedGroup: (group: SelectedGroup) => void;
  companyOptions?: CompanyOption[];
  networkOptions?: NetworkOption[];
  byNetwork: boolean;
  setByNetwork: (byNetwork: boolean) => void;
  dates: DateRange;
  setDates: (dates: DateRange) => void;
}

const DeviceBreakoutActions = ({
  selectedGroup,
  setSelectedGroup,
  companyOptions,
  networkOptions,
  byNetwork,
  setByNetwork,
  dates,
  setDates
}: DeviceBreakoutActionsProps): ReactElement => (
  <div className='device-breakout-actions'>
    <DeviceBreakoutGroupPicker
      selectedGroup={selectedGroup}
      setSelectedGroup={setSelectedGroup}
      companyOptions={companyOptions}
      networkOptions={networkOptions}
    />
    <SegmentedControlButton
      size={Size.LG}
      options={[
        { key: 'company', label: 'Company' },
        { key: 'network', label: 'Network' }
      ]}
      selectedOption={byNetwork ? 'network' : 'company'}
      onChange={key => setByNetwork(key === 'network')}
    />
    <DatePicker range={dates} onChange={setDates} isOutsideRange={() => false} />
  </div>
);

interface DeviceBreakoutBodyProps {
  noOptionsMessage?: string;
  selectedData?: DeviceCell[][];
  topData?: string[];
  leftData?: string[];
  bottomData?: string[];
  cellRenderer: CellRenderer<DeviceCell>;
  topRenderer: CellRenderer<string>;
}

const DeviceBreakoutBody = ({
  noOptionsMessage,
  selectedData,
  topData,
  leftData,
  bottomData,
  cellRenderer,
  topRenderer
}: DeviceBreakoutBodyProps): ReactElement => {
  if (noOptionsMessage) {
    return (
      <div className='device-breakout-empty-state'>
        <TableEmptyState message={noOptionsMessage} />
      </div>
    );
  }

  if (!selectedData) {
    return (
      <Skeleton>
        <TableSkeleton rowHeight={ROW_HEIGHT} headerHeight={HEADER_HEIGHT} />
      </Skeleton>
    );
  }

  if (!selectedData.length) {
    return (
      <div className='device-breakout-empty-state'>
        <TableEmptyState message='This client has no device data in this date range.' />
      </div>
    );
  }

  return (
    <div className='device-breakout-table'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <StickyTable
            width={width}
            height={height}
            leftWidth={LEFT_WIDTH}
            rowHeight={ROW_HEIGHT}
            columnWidth={COLUMN_WIDTH}
            topHeight={TOP_HEIGHT}
            data={selectedData}
            topData={topData}
            leftData={leftData}
            bottomData={bottomData}
            cellRenderer={cellRenderer}
            topRenderer={topRenderer}
          />
        )}
      />
    </div>
  );
};

export const DeviceBreakout = (): ReactElement => {
  const setError = useSetError();

  const [dates, setDates] = useState<DateRange>(initDates);
  const [byNetwork, setByNetworkRaw] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SelectedGroup>();

  const companyOptionsQuery = useCompanyOptions();
  const networkOptionsQuery = useNetworkOptions(dates);
  const companyOptions = companyOptionsQuery.data;
  const networkOptions = networkOptionsQuery.data;

  useEffect(() => {
    if (selectedGroup) {
      return;
    }
    if (byNetwork) {
      if (networkOptions?.[0]) {
        setSelectedGroup(networkOptions[0]);
      }
    } else if (companyOptions?.[0]) {
      setSelectedGroup(companyOptions[0]);
    }
  }, [byNetwork, companyOptions, networkOptions, selectedGroup]);

  const setByNetwork = useCallback(
    (nextByNetwork: boolean) => {
      setSelectedGroup(nextByNetwork ? networkOptions?.[0] : companyOptions?.[0]);
      setByNetworkRaw(nextByNetwork);
    },
    [networkOptions, companyOptions]
  );

  const companyTableQuery = useDeviceBreakoutByCompany(
    dates,
    !byNetwork && selectedGroup && 'cid' in selectedGroup ? selectedGroup.cid : false
  );
  const networkTableQuery = useDeviceBreakoutByNetwork(
    dates,
    byNetwork && selectedGroup && 'network' in selectedGroup
      ? (selectedGroup.derived_id ?? selectedGroup.network)
      : false,
    byNetwork && selectedGroup && 'network' in selectedGroup ? selectedGroup.platform : ''
  );
  const tableDataQuery = byNetwork ? networkTableQuery : companyTableQuery;
  const { topData, leftData, bottomData, selectedData } = useMemo(
    () => buildTableRows(tableDataQuery.data),
    [tableDataQuery.data]
  );

  let noOptionsMessage: string | undefined;
  if (byNetwork && networkOptionsQuery.isSuccess && !networkOptions?.length) {
    noOptionsMessage = 'No networks found for this date range.';
  } else if (!byNetwork && companyOptionsQuery.isSuccess && !companyOptions?.length) {
    noOptionsMessage = 'No companies found for this date range.';
  }

  const companyFlightDataQuery = useCompanyFlightData(
    dates,
    !byNetwork && selectedGroup && 'cid' in selectedGroup ? selectedGroup.cid : false
  );
  const { flightMap } = companyFlightDataQuery.data ?? {};

  const queryError =
    companyOptionsQuery.error ||
    networkOptionsQuery.error ||
    tableDataQuery.error ||
    companyFlightDataQuery.error;
  useEffect(() => {
    if (queryError) {
      setError({ message: queryError.message, reportError: queryError });
    }
  }, [queryError, setError]);

  const [modalData, setModalData] = useState<ModalData>();

  const cellRenderer: CellRenderer<DeviceCell> = useCallback(
    ({ data, style, classes }) => {
      if (data.count === '-') {
        return (
          <div className={cn(...classes, 'device-breakout-selected-cell')} style={style}>
            <div>{data.count}</div>
          </div>
        );
      }
      return (
        <InteractiveDiv
          className={cn(...classes, 'device-breakout-selected-cell', {
            'with-data': !byNetwork
          })}
          style={style}
          disabled={byNetwork}
          onClick={() => {
            if (!companyTableQuery.data) {
              return;
            }
            const { combinedPlacementName, network, placement } = data;
            const flights = combinedPlacementName
              ? (companyTableQuery.data.placementToFlightsMap[combinedPlacementName] ?? [])
              : [];
            setModalData({ flights, network, placement });
          }}
        >
          <div className='device-breakout-cell-count'>
            {data.count}
            <span className='device-breakout-cell-percentage'>{data.percentage}</span>
          </div>
        </InteractiveDiv>
      );
    },
    [byNetwork, companyTableQuery.data]
  );

  const topRenderer: CellRenderer<string> = useCallback(
    ({ data, style, classes }) => {
      let label = '';
      if (!byNetwork) {
        const parts = data.split(' ');
        // Gets rid of the short code
        parts.shift();
        // If it's Streaming _____, then just use _____. If it's just Streaming, leave it.
        if (parts[0] === 'Streaming' && parts.length > 1) {
          parts.shift();
        }
        label = parts.join(' ');
      }
      return (
        <OverlayTrigger placement={Placement.BOTTOM} overlay={<Tooltip>{data}</Tooltip>}>
          <div className={cn(...classes, 'device-breakout-top-cell')} style={style}>
            <EntityLogo
              kind={byNetwork ? EntityKind.COMPANY : EntityKind.NETWORK}
              id={byNetwork ? data : data.split(' ')[0]}
              size={Size.LG}
            />
            {!byNetwork && <div className='platform'>{label}</div>}
          </div>
        </OverlayTrigger>
      );
    },
    [byNetwork]
  );

  return (
    <Page
      title='Device Breakout'
      pageType='Device Breakout'
      isLoading={!selectedData && !noOptionsMessage}
      actions={
        <DeviceBreakoutActions
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          companyOptions={companyOptions}
          networkOptions={networkOptions}
          byNetwork={byNetwork}
          setByNetwork={setByNetwork}
          dates={dates}
          setDates={setDates}
        />
      }
    >
      <div className='device-breakout'>
        <DeviceBreakoutBody
          noOptionsMessage={noOptionsMessage}
          selectedData={selectedData}
          topData={topData}
          leftData={leftData}
          bottomData={bottomData}
          cellRenderer={cellRenderer}
          topRenderer={topRenderer}
        />
      </div>
      {modalData && (
        <DeviceBreakoutModal
          data={modalData}
          flightMap={flightMap}
          onClose={() => setModalData(undefined)}
        />
      )}
    </Page>
  );
};
